import { useState, useCallback, useEffect, useRef } from 'react';
import type { UploadedFile } from '../types';
import { uploadFile, deleteUploadedFile } from '../api/upload';
import { recordEvent } from '@workspace/observability/browser';

const IMAGE_MIME_TYPES = new Set([
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/svg+xml',
]);

// Files larger than this will use presigned URL upload
const PRESIGNED_UPLOAD_THRESHOLD = 1 * 1024 * 1024; // 1MB

/**
 * Largest file the storage service will accept.
 *
 * Matches `express.raw({ limit: "50mb" })` in `apps/api/storage`. Checked here
 * as well because without it an oversized file is uploaded in full, rejected
 * with a 413, and then silently degrades to riding INLINE as base64 — so the
 * worst case (a huge file) takes the most expensive path twice and lands in
 * the message body anyway.
 *
 * A little under 50MB, since the server's limit applies to the whole request
 * and the headers count toward it.
 */
const MAX_UPLOAD_BYTES = 49 * 1024 * 1024;

/** Which picker to open. See the note in `handleFileSelect` — on mobile this
 *  selects a different app, not merely a filter. */
export type FilePickerMode = 'images' | 'files' | 'any';

export interface FilePickerOptions {
  mode?: FilePickerMode;
  /** Jump straight to the camera instead of the library (mobile only). */
  capture?: 'user' | 'environment';
}

/**
 * Extensions offered by the file picker.
 *
 * Built from two sources: every format the kreuzberg extraction server reports
 * via `list_formats` (so we never offer a document nothing downstream can read),
 * plus the common source-code extensions from the popular GitHub languages.
 *
 * WHY EXTENSIONS AND NOT JUST MIME WILDCARDS
 *
 * `text/*` looks like it should cover source code. It does not. Checked against
 * the system MIME database: `.html` and `.css` map to text/* and would be
 * reachable, but `.md`, `.toml`, `.yaml`, `.go`, `.rb`, `.php`, `.cs`, `.kt`,
 * `.swift`, `.vue`, `.svelte`, `.lua` and most others have NO mapping at all, so
 * no wildcard can ever reach them. Two are actively wrong: `.ts` maps to
 * video/mp2t (MPEG transport stream) and `.rs` to application/rls-services+xml.
 *
 * So the long list is not belt-and-braces — for most code files an explicit
 * extension is the only thing that works.
 *
 * PLATFORMS DIVERGE, WHICH IS WHY BOTH FORMS ARE LISTED
 *
 * `accept` entries are matched differently per platform: iOS resolves them
 * largely by extension, desktop browsers lean on the MIME database. An entry
 * that works on one can silently do nothing on the other — observed with
 * `.html`, which `text/*` should have covered but iOS did not offer. Listing
 * wildcards AND explicit extensions means each platform finds something it
 * understands, rather than tuning for whichever was tested last.
 *
 * NOTE: `accept` only filters the picker. Drag-drop and paste bypass it
 * entirely, and nothing re-checks the type on upload — so this list is a
 * convenience, not a security boundary.
 */
const DOCUMENT_ACCEPT =
  // MIME wildcards first: cheap, and the only thing that catches text files
  // whose extension isn't listed below.
  'application/pdf,application/json,text/*,' +
  '.7z,.adoc,.astro,.bash,.bib,.bmp,.c,.cc,.cfg,.cjs,' +
  '.clj,.cljs,.commonmark,.conf,.cpp,.cr,.cs,.css,.csv,.cxx,' +
  '.dart,.dbf,.dbk,.diff,.djot,.doc,.docbook,.docm,.docx,.dot,' +
  '.dotm,.dotx,.edn,.eml,.env,.enw,.epub,.erl,.ex,.exs,' +
  '.f90,.fb2,.fish,.fs,.gif,.go,.gql,.gradle,.graphql,.groovy,' +
  '.gz,.h,.hh,.hpp,.hrl,.hs,.htm,.html,.hwp,.hwpx,' +
  '.ini,.ipynb,.j2c,.j2k,.jats,.java,.jb2,.jbig2,.jl,.jp2,' +
  '.jpeg,.jpg,.jpm,.jpx,.js,.json,.jsonl,.jsx,.key,.kt,' +
  '.kts,.latex,.less,.log,.lua,.m,.markdown,.md,.mdx,.mj2,' +
  '.mjs,.mm,.msg,.nbib,.ndjson,.nim,.numbers,.ods,.odt,.opml,' +
  '.org,.pages,.pas,.patch,.pbm,.pdf,.pgm,.php,.pl,.pm,' +
  '.png,.pnm,.pot,.potm,.potx,.ppm,.ppsx,.ppt,.pptm,.pptx,' +
  '.prisma,.properties,.proto,.ps1,.psm1,.pst,.py,.pyi,.r,.rb,' +
  '.ris,.rkt,.rs,.rst,.rtf,.sass,.sc,.scala,.scss,.sh,' +
  '.sol,.sql,.svelte,.svg,.swift,.tar,.tex,.tf,.tfvars,.tgz,' +
  '.tif,.tiff,.toml,.ts,.tsv,.tsx,.txt,.typ,.typst,.vb,' +
  '.vue,.webp,.xla,.xlam,.xls,.xlsb,.xlsm,.xlsx,.xlt,.xltx,' +
  '.xml,.yaml,.yml,.zig,.zip,.zsh';

const ACCEPT_BY_MODE: Record<FilePickerMode, string> = {
  images: 'image/*',
  files: DOCUMENT_ACCEPT,
  any: `image/*,${DOCUMENT_ACCEPT}`,
};

function isImageFile(file: File): boolean {
  return IMAGE_MIME_TYPES.has(file.type) || file.type.startsWith('image/');
}

interface UseFileUploadOptions {
  apiUrl?: string; // Required for presigned uploads
  /**
   * Write an attached file into the user's artifact store, returning its key.
   *
   * This is the upload that MATTERS: it is the store `edit_image` resolves
   * `image_key` through, and the one the user can see and delete. Running it on
   * attach rather than on send means the bytes are in place before the model is
   * ever told the key, which is what removes the "image wasn't ready" race.
   *
   * Returns null when the file was not persisted — a private or unauthenticated
   * session, or a storage failure. All are normal outcomes: the attachment
   * still sends inline, it just cannot be edited.
   */
  persistOnAttach?: (
    file: File,
    fileId: string,
    onProgress: (percent: number) => void,
  ) => Promise<string | null>;
  /**
   * Remove a previously-persisted artifact, by key.
   *
   * Used when the user removes or replaces an attachment before sending: the
   * bytes are already in their storage, and leaving them there would persist
   * content they explicitly took back.
   */
  deleteArtifact?: (storageKey: string) => Promise<boolean>;
  /**
   * A file was refused before any upload — currently only for being too large.
   *
   * Reported through a callback rather than swallowed, because the alternative
   * is an attachment that simply never appears: the picker closes, nothing is
   * added, and the only trace is a console line nobody is watching.
   */
  onRejected?: (message: string) => void;
  /**
   * Whether drag-and-drop onto the window attaches files.
   *
   * Defaults to true. Set false where the host has disabled file upload — the
   * listeners must not be registered at all in that case, or dropping a file
   * would be silently swallowed (preventDefault stops the browser's own
   * navigation) while nothing attaches it.
   */
  enableDropzone?: boolean;
}

export function useFileUpload(options: UseFileUploadOptions = {}) {
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const apiUrlRef = useRef(options.apiUrl);
  apiUrlRef.current = options.apiUrl;

  // Held in a ref so the picker callback never goes stale, and so this hook can
  // be called before the session/artifact hooks it depends on are constructed.
  const persistRef = useRef(options.persistOnAttach);
  persistRef.current = options.persistOnAttach;
  const deleteArtifactRef = useRef(options.deleteArtifact);
  deleteArtifactRef.current = options.deleteArtifact;
  const onRejectedRef = useRef(options.onRejected);
  onRejectedRef.current = options.onRejected;

  /**
   * Take a set of Files and stage them as attachments.
   *
   * Shared by the picker and by drag-and-drop, so a dropped file goes through
   * exactly the same persistence, withdrawal and failure handling as a picked
   * one. Duplicating this for drop is how the two paths would drift.
   */
  const addFiles = useCallback(async (incoming: File[]) => {
    for (const file of incoming) {
      const fileId = Math.random().toString(36).substring(7);
      const isImage = isImageFile(file);

      const dataUrl = await readFileAsDataURL(file);

      // Too big to store, so do not try. The upload would spend the whole
      // transfer only to be refused, and the fallback would then put the same
      // bytes inline in the message — the one place they least belong.
      if (file.size > MAX_UPLOAD_BYTES) {
        console.error(
          `[file-upload] "${file.name}" is ${(file.size / 1024 / 1024).toFixed(1)}MB, ` +
            `over the ${(MAX_UPLOAD_BYTES / 1024 / 1024).toFixed(0)}MB limit — not attached.`,
        );
        recordEvent(
          'chat.attachment.too_large',
          { file_type: file.type || 'unknown', size_kb: Math.round(file.size / 1024) },
          { error: true },
        );
        onRejectedRef.current?.(
          `"${file.name}" is too large to attach (${(file.size / 1024 / 1024).toFixed(1)}MB). ` +
            `The limit is ${(MAX_UPLOAD_BYTES / 1024 / 1024).toFixed(0)}MB.`,
        );
        continue;
      }

      const canPersist = !!persistRef.current;
      const needsPresignedUpload =
        !canPersist && file.size > PRESIGNED_UPLOAD_THRESHOLD && !!apiUrlRef.current;
      const willUpload = canPersist || needsPresignedUpload;

      const uploadedFile: UploadedFile = {
        id: fileId,
        name: file.name,
        size: file.size,
        type: file.type || 'application/octet-stream',
        dataUrl,
        isImage,
        uploading: willUpload,
        uploadProgress: willUpload ? 0 : undefined,
      };

      setFiles((prev) => [...prev, uploadedFile]);

      if (canPersist) {
        try {
          const storageKey = await persistRef.current!(file, fileId, (percent) => {
            setFiles((prev) =>
              prev.map((f) => (f.id === fileId ? { ...f, uploadProgress: percent } : f)),
            );
          });
          setFiles((prev) =>
            prev.map((f) =>
              f.id === fileId
                ? { ...f, storageKey: storageKey ?? undefined, uploading: false, uploadProgress: 100 }
                : f,
            ),
          );
        } catch (error) {
          console.error('Failed to persist attachment:', error);
          recordEvent(
            'chat.attachment.persist_failed',
            { file_type: file.type || 'unknown', size_kb: Math.round(file.size / 1024) },
            { error: true },
          );
          setFiles((prev) =>
            prev.map((f) =>
              f.id === fileId ? { ...f, uploading: false, uploadProgress: undefined } : f,
            ),
          );
        }
      } else if (needsPresignedUpload) {
        try {
          const { downloadUrl, deleteUrl } = await uploadFile(
            apiUrlRef.current!,
            file,
            (progress) => {
              setFiles((prev) =>
                prev.map((f) =>
                  f.id === fileId ? { ...f, uploadProgress: progress.percent } : f,
                ),
              );
            },
          );
          setFiles((prev) =>
            prev.map((f) =>
              f.id === fileId
                ? { ...f, downloadUrl, deleteUrl, uploading: false, uploadProgress: 100 }
                : f,
            ),
          );
        } catch (error) {
          console.error('Failed to upload file:', error);
          recordEvent(
            'chat.upload.failed',
            { file_type: file.type || 'unknown', size_kb: Math.round(file.size / 1024) },
            { error: true },
          );
          setFiles((prev) => prev.filter((f) => f.id !== fileId));
        }
      }
    }
  }, []);

  const handleFileSelect = useCallback((opts: FilePickerOptions = {}) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = true;
    // `accept` is what decides WHICH picker a phone opens, not just what it
    // filters: "image/*" surfaces the photo library, while the broad list below
    // opens Files / iCloud Drive. On desktop it only filters, which is why this
    // distinction is easy to miss when testing.
    input.accept = ACCEPT_BY_MODE[opts.mode ?? 'any'];
    // `capture` asks the OS to skip the library and open the camera directly.
    // Only meaningful on mobile; desktop browsers ignore it.
    if (opts.capture) input.capture = opts.capture;

    input.onchange = async (e) => {
      const target = e.target as HTMLInputElement;
      if (!target.files) return;
      await addFiles(Array.from(target.files));
    };

    input.click();
  }, [addFiles]);

  /**
   * Withdraw the bytes behind these files, wherever they went.
   *
   * Called when an attachment is removed, replaced, or the composer is cleared
   * WITHOUT sending. Both upload paths are covered: the artifact store (the
   * usual one now) and the presigned LB upload (unauthenticated/private
   * fallback). A file that never left the browser has neither and is a no-op.
   *
   * This is the half that makes eager upload honest — the user believes an
   * attachment they took back never left their machine.
   */
  const withdrawUploads = useCallback((toWithdraw: UploadedFile[]) => {
    for (const f of toWithdraw) {
      if (f.storageKey) void deleteArtifactRef.current?.(f.storageKey);
      if (f.deleteUrl) void deleteUploadedFile(f.deleteUrl);
    }
  }, []);

  /**
   * Whether a file is currently being dragged over the window.
   *
   * Drives the drop overlay. Tracked with a COUNTER rather than a boolean,
   * because `dragenter`/`dragleave` fire for every element the pointer crosses
   * — moving over a child fires leave-then-enter, and a boolean flickers the
   * overlay off and on the whole way across the page.
   */
  const [isDraggingFiles, setIsDraggingFiles] = useState(false);
  const dragDepth = useRef(0);

  const dropzoneEnabled = options.enableDropzone ?? true;

  useEffect(() => {
    if (typeof window === 'undefined' || !dropzoneEnabled) return;

    /**
     * Only react to an actual FILE drag.
     *
     * Dragging selected text, a link, or an image within the page all fire the
     * same events; without this check the overlay appears when someone drags a
     * word across their own message. `types` is the one part of DataTransfer
     * readable during a drag — the files themselves are deliberately not, until
     * drop.
     */
    const hasFiles = (e: DragEvent) =>
      Array.from(e.dataTransfer?.types ?? []).includes('Files');

    const onDragEnter = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      dragDepth.current += 1;
      setIsDraggingFiles(true);
    };

    const onDragOver = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      // Both required: without preventDefault the browser NAVIGATES to the
      // dropped file, replacing the conversation with a raw image. It has to
      // be called on dragover specifically, not just on drop.
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
    };

    const onDragLeave = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      dragDepth.current = Math.max(0, dragDepth.current - 1);
      if (dragDepth.current === 0) setIsDraggingFiles(false);
    };

    const onDrop = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      dragDepth.current = 0;
      setIsDraggingFiles(false);

      const dropped = Array.from(e.dataTransfer?.files ?? []);
      if (dropped.length > 0) void addFiles(dropped);
    };

    // A drag that leaves the window entirely fires no final `dragleave`, so the
    // counter can be left stranded above zero and the overlay stuck on. The
    // window's own dragleave (relatedTarget null) and a global drop elsewhere
    // both reset it.
    const onWindowDragLeave = (e: DragEvent) => {
      if (e.relatedTarget === null) {
        dragDepth.current = 0;
        setIsDraggingFiles(false);
      }
    };

    window.addEventListener('dragenter', onDragEnter);
    window.addEventListener('dragover', onDragOver);
    window.addEventListener('dragleave', onDragLeave);
    window.addEventListener('drop', onDrop);
    window.addEventListener('dragleave', onWindowDragLeave);

    return () => {
      window.removeEventListener('dragenter', onDragEnter);
      window.removeEventListener('dragover', onDragOver);
      window.removeEventListener('dragleave', onDragLeave);
      window.removeEventListener('drop', onDrop);
      window.removeEventListener('dragleave', onWindowDragLeave);
    };
  }, [addFiles, dropzoneEnabled]);

  const removeFile = useCallback((id: string) => {
    setFiles((prev) => {
      const going = prev.filter((f) => f.id === id);
      withdrawUploads(going);
      return prev.filter((f) => f.id !== id);
    });
  }, [withdrawUploads]);

  /**
   * Clear the composer's attachments and delete anything already uploaded.
   *
   * For use when the attachments are being ABANDONED. After a successful send
   * the files are in the conversation and must NOT be withdrawn — use
   * `clearFilesAfterSend` for that.
   */
  const clearFiles = useCallback(() => {
    setFiles((prev) => {
      withdrawUploads(prev);
      return [];
    });
  }, [withdrawUploads]);

  /**
   * Clear the composer after the attachments have been SENT.
   *
   * Deliberately does not withdraw anything: the files are now part of the
   * conversation and the message references their download URLs. Calling
   * `clearFiles` here would delete the bytes out from under the message that
   * just went out.
   */
  const clearFilesAfterSend = useCallback(() => {
    setFiles([]);
  }, []);

  // Check if any files are still uploading
  const isUploading = files.some((f) => f.uploading);

  return {
    files,
    handleFileSelect,
    isDraggingFiles,
    clearFilesAfterSend,
    removeFile,
    clearFiles,
    isUploading,
  };
}

async function readFileAsDataURL(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target?.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}
